// src/api/axiosInstance.ts
/**
 * The shared axios instance: every screen in this app that is not campaigns, invoicing or the
 * security panel makes its calls through here.
 *
 * The predicates that decide whether a failed request means "this session is over" used to live in
 * this file. They now live in `sessionErrors.ts`, which imports neither axios nor the store and can
 * therefore be unit-tested — see the note at the top of that file. `isSessionOver` is re-exported
 * below because `campaignApi` and `invoicingApi` import it from here.
 */
import axios from 'axios';
import config from '../config';
import { store } from '../store/store';
import { logout } from '../reducers/authSlice';
import { stepUpRequired } from '../store/mfaSlice';
import { isMfaRequired } from './errors';
import { shouldSharedApiLogout } from './sessionErrors';

/** Kept as an export of this module: `campaignApi` and `invoicingApi` both `import { isSessionOver }
 *  from './axiosInstance'`, and their own rule is the narrow one, not the shared one below. */
export { isSessionOver } from './sessionErrors';

// Create an Axios instance
const api = axios.create({
    baseURL: config.API_URL,
});

// Add a request interceptor
api.interceptors.request.use(
    (requestConfig) => {
        const state = store.getState(); // Access the Redux state directly
        const token = state.auth.accessToken;

        // Exclude login and register endpoints from having the Authorization header
        if (token && !requestConfig.url?.includes('/login') && !requestConfig.url?.includes('/register')) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        return requestConfig;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add a response interceptor to handle errors like token expiration
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        // On the auth endpoints themselves a 401 means "wrong e-mail or password" and the login form
        // shows its own message. Logging out there would be answering a failed sign-in by signing
        // the user out, so nothing below applies to them.
        if (error.config?.url?.includes('/v1/auth')) {
            return Promise.reject(error);
        }

        // WHY THERE IS NO LONGER A PLAIN-403 ARM HERE.
        //
        // This interceptor used to sign the user out on ANY 403, on the reasoning that no screen had
        // a use for a 403 of its own. Comment moderation uses this instance and always answers
        // `403 {"error":"mfa_required"}` to an admin whose session has not passed its second factor.
        // Under the old rule the owner's own admin page would have signed them out of the whole
        // site, with no way to tell that from a real expiry.
        //
        // The rule is now the body's: only a 403 that names a dead token, or one with no readable
        // JSON body at all, ends the session (`shouldSharedApiLogout`, tested in
        // sessionErrors.test.ts). That is the same rule the installed mobile build already runs, so
        // the two clients agree; `forbidden`, `rate_limited` and `mfa_required` reach the caller
        // with the session intact.
        if (await shouldSharedApiLogout(error)) {
            store.dispatch(logout()); // Log the user out
        } else if (isMfaRequired(error)) {
            // Enforcement flipped on, or this token's verification was cleared server-side, while
            // the page was open. Telling the store lets the one modal host ask for a code instead of
            // every screen inventing its own recovery from a 403. (Blob error bodies are not decoded
            // by `isMfaRequired`; the only Blob call on this instance is the legacy invoice PDF,
            // which is not an @AdminStepUp endpoint.)
            store.dispatch(stepUpRequired());
        }
        return Promise.reject(error);
    }
);

export default api;
